import { z } from "zod";

export const BROADCAST_STATES = [
  "draft",
  "paused",
  "queued",
  "scheduled",
  "resend_scheduled",
  "resend_queued",
  "sent",
  "resent",
  "completed",
  "resend_completed",
  "no_resend_scheduled",
  "failed_cap_reached",
  "cancelled",
  "archived",
] as const;

export const broadcastStateSchema = z.enum(BROADCAST_STATES);

export type BroadcastState = z.infer<typeof broadcastStateSchema>;

export const BROADCAST_STATUS_CATEGORIES = [
  "All",
  "Completed",
  "Draft",
  "Scheduled",
  "Archived",
] as const;

export type BroadcastStatusCategory =
  (typeof BROADCAST_STATUS_CATEGORIES)[number];

type FilterableCategory = Exclude<BroadcastStatusCategory, "All">;

const STATUS_CATEGORY_STATES: Record<FilterableCategory, BroadcastState[]> = {
  Completed: [
    "completed",
    "sent",
    "resent",
    "resend_completed",
    "no_resend_scheduled",
  ],
  Draft: ["draft"],
  Scheduled: ["scheduled", "queued", "resend_scheduled", "resend_queued"],
  Archived: ["archived"],
};

/**
 * Maps a backend broadcast state to its user-facing category.
 * Returns "All" if the state doesn't belong to a specific category
 * (e.g. "paused", "failed_cap_reached", "cancelled").
 */
const FILTERABLE_CATEGORIES: FilterableCategory[] = [
  "Completed",
  "Draft",
  "Scheduled",
  "Archived",
];

export function getBroadcastStatusCategory(
  state: BroadcastState,
): BroadcastStatusCategory {
  for (const category of FILTERABLE_CATEGORIES) {
    if (STATUS_CATEGORY_STATES[category].includes(state)) {
      return category;
    }
  }
  return "All";
}

/**
 * Returns the backend states for a given category, or undefined for "All"
 * (which means no filter -- the API returns everything).
 */
export function getStatesForCategory(
  category: BroadcastStatusCategory,
): BroadcastState[] | undefined {
  if (category === "All") return undefined;
  return STATUS_CATEGORY_STATES[category];
}
